import React from 'react';
import { isNil } from 'lodash';
import TableFilterInput from '../../../../../components/table/TableFilterInput';
import { addAllItemToSelectList, setColumnWidth, setDash, setNumberWithComma } from '../../../../../lib/common';
import Select from '../../../../../components/input/Select/Select';
import {
  COUPON_DISCOUNT_TYPE_V2,
  COUPON_STATUS_V2,
  COUPON_TYPE_V2,
  CouponItemV2,
  CouponListRequest,
} from '../../../../../types/coupons';
import { setDateFormat } from '../../../../../lib/moment';
import { DATE_FORMAT, DateRageType } from '../../../../../types/common/date';
import Anchor from '../../../../../components/link/Anchor';
import { Columns } from '../../../../../types/common/table';
import {
  COUPON_DISCOUNT_TYPE_LABEL_V2,
  COUPON_DISCOUNT_TYPE_SELECT_LIST_V2,
  COUPON_DISCOUNT_UNIT_V2,
  COUPON_STATUS_LABEL_V2,
  COUPON_STATUS_SELECT_LIST_V2,
  COUPON_TYPE_LABEL_V2,
  COUPON_TYPE_SELECT_LIST_V2,
} from '../../../../../constants/service/app/couponConstantsV2';
import DateRangePicker from '../../../../../components/datePicker/DateRangePicker';
import { getCouponPeriodText } from '../utils/getCouponPeriodText';

export const couponReactTableColumns: Columns<CouponItemV2, CouponListRequest> = ({ filterOption, onFilter, list }) => {
  const filterable = !!filterOption;

  return [
    {
      Header: '쿠폰 ID',
      accessor: 'id',
      filterable,
      width: 80,
      Cell: (row) => (
        <Anchor color="primary" isUnderLine text={row.value} href={`/coupon/detail/${row.value}`} target="_blank" />
      ),
      Filter: () => (
        <TableFilterInput
          type="number"
          option={filterOption.id}
          onFilter={(id: string): void => onFilter({ id: isNil(id) ? id : Number(id) })}
        />
      ),
    },
    {
      Header: '쿠폰명',
      accessor: 'name',
      filterable,
      width: setColumnWidth(list, 'name') + 20,
      Filter: () => <TableFilterInput option={filterOption.name} onFilter={(name: string) => onFilter({ name })} />,
    },
    {
      Header: '쿠폰 설명',
      accessor: 'description',
      filterable,
      width: setColumnWidth(list, 'description') + 40,
      Cell: (row) => setDash(row.value),
      Filter: () => (
        <TableFilterInput
          option={filterOption.description}
          onFilter={(description: string) => onFilter({ description })}
        />
      ),
    },
    {
      accessor: 'discountType',
      Header: '쿠폰 혜택',
      filterable,
      width: 200,
      Cell: (row: { value: COUPON_DISCOUNT_TYPE_V2; original: CouponItemV2 }) =>
        `${COUPON_DISCOUNT_TYPE_LABEL_V2[row.value]} ${setNumberWithComma(
          row.original.benefit.discountAmount,
          COUPON_DISCOUNT_UNIT_V2[row.value],
        )}` || '_',
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_DISCOUNT_TYPE_SELECT_LIST_V2)}
          onChange={(discountType: COUPON_DISCOUNT_TYPE_V2) => onFilter({ discountType })}
          defaultValue={filterOption.discountType || null}
          value={filterOption.discountType}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      Header: '쿠폰상태',
      accessor: 'status',
      filterable,
      width: 100,
      Cell: (row) => COUPON_STATUS_LABEL_V2[row.value as COUPON_STATUS_V2],
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_STATUS_SELECT_LIST_V2)}
          onChange={(status: COUPON_STATUS_V2) => onFilter({ status })}
          defaultValue={filterOption.status || null}
          value={filterOption.status}
          isTable={true}
        />
      ),
    },
    {
      Header: '쿠폰유형',
      accessor: 'type',
      filterable,
      width: 200,
      Cell: (row) => COUPON_TYPE_LABEL_V2[row.value as COUPON_TYPE_V2],
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_TYPE_SELECT_LIST_V2)}
          onChange={(type: COUPON_TYPE_V2) => onFilter({ type })}
          defaultValue={filterOption.type || null}
          value={filterOption.type}
          isTable={true}
        />
      ),
    },
    {
      Header: '24K 상품 제외 여부',
      accessor: 'isExclude24k',
      Cell: (row) => (row.value ? '24k 상품 제외' : '-'),
      width: 150,
    },
    {
      Header: '쿠폰혜택 정보',
      accessor: 'benefit',
      resizable: true,
      width: 200,
      Cell: (row) => {
        const maxDiscountText =
          row.original.discountType === COUPON_DISCOUNT_TYPE_V2.RATE
            ? `\n(최대 ${setNumberWithComma(row.value.maximumDiscountAmount)}원)`
            : '';

        return (
          <div className="multi-line">
            {`${setNumberWithComma(row.value.minimumPurchaseAmount)}원 이상 구매시\n(${setNumberWithComma(
              row.value.discountAmount,
              COUPON_DISCOUNT_UNIT_V2[row.original.discountType as COUPON_DISCOUNT_TYPE_V2],
            )} 할인 쿠폰)${maxDiscountText}`}
          </div>
        );
      },
    },
    {
      Header: '생성일자',
      accessor: 'createDate',
      filterable,
      width: 200,
      Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_DASH_FORMAT),
      Filter: () => (
        <DateRangePicker
          value={filterOption.createDate}
          outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
          onSelect={(availablePeriod: DateRageType) =>
            onFilter({ createDate: availablePeriod as { startDate: string; endDate: string } })
          }
        />
      ),
    },
    {
      Header: '사용기간',
      accessor: 'availablePeriod',
      resizable: true,
      filterable,
      width: 350,
      Cell: (row) => getCouponPeriodText(row.value),
      Filter: () => (
        <DateRangePicker
          value={filterOption.availablePeriod}
          outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
          onSelect={(availablePeriod: DateRageType) =>
            onFilter({ availablePeriod: availablePeriod as { startDate: string; endDate: string } })
          }
        />
      ),
    },
  ];
};
